import React, { useState, useEffect } from 'react';
import { Container, Card, Form, InputGroup, Button, Row, Col, Toast, ToastContainer, Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, push } from 'firebase/database';
import { Car } from '../types/car';

interface Fill {
  timestamp: number;
  odometer: number;
  fuelamount: number;
  price: number;
}

type ConsumptionTrend = 'up' | 'down' | 'stable';

const Moscht = () => {
  const { currentUser } = useAuth();
  const [ownedCarIds, setOwnedCarIds] = useState<string[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [selectedCar, setSelectedCar] = useState<string>('');
  const [mileage, setMileage] = useState('');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [fills, setFills] = useState<Fill[]>([]);
  const [avgConsumption, setAvgConsumption] = useState<number>(0);
  const [consumptionTrend, setConsumptionTrend] = useState<ConsumptionTrend>('stable');
  const [totalStats, setTotalStats] = useState<{
    distance: number;
    fuel: number;
    fillCount: number;
    totalCost: number;
    costPer100km: number;
    avgPricePerLiter: number;
  }>({
    distance: 0,
    fuel: 0,
    fillCount: 0,
    totalCost: 0,
    costPer100km: 0,
    avgPricePerLiter: 0
  });
  const [showToast, setShowToast] = useState(false);
  const [lastConsumption, setLastConsumption] = useState<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    // Fetch owned car IDs
    const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
    const ownedCarsUnsubscribe = onValue(ownedCarsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedCarIds(Object.values(data));
    });

    // Fetch all cars data
    const carsRef = ref(database, 'cars');
    const carsUnsubscribe = onValue(carsRef, (snapshot) => {
      const data = snapshot.val();
      setCars(data || {});
    });

    // Fetch selected car
    const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
    const selectedCarUnsubscribe = onValue(selectedCarRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedCar(data || '');
    });

    return () => {
      ownedCarsUnsubscribe();
      carsUnsubscribe();
      selectedCarUnsubscribe();
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !selectedCar) {
      setFills([]);
      setAvgConsumption(0);
      setConsumptionTrend('stable');
      setTotalStats({
        distance: 0,
        fuel: 0,
        fillCount: 0,
        totalCost: 0,
        costPer100km: 0,
        avgPricePerLiter: 0
      });
      return;
    }

    // Fetch fills for selected car
    const fillsRef = ref(database, `cars/${selectedCar}/fills`);
    const unsubscribe = onValue(fillsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const fillsList = Object.values(data) as Fill[];
      setFills(fillsList.sort((a, b) => a.timestamp - b.timestamp));

      // Calculate average consumption
      if (fillsList.length >= 2) {
        let totalConsumption = 0;
        let validPairs = 0;
        let totalDistance = 0;
        let totalFuel = 0;
        let totalCost = 0;
        let lastConsumptions: number[] = [];

        // Add first fill's fuel and cost
        totalFuel += parseFloat(fillsList[0].fuelamount.toString());
        totalCost += parseFloat(fillsList[0].price.toString());

        for (let i = 1; i < fillsList.length; i++) {
          const distance = parseFloat((fillsList[i].odometer - fillsList[i-1].odometer).toString());
          if (distance > 0) {
            const fuelAmount = parseFloat(fillsList[i].fuelamount.toString());
            const consumption = (fuelAmount / distance) * 100;
            totalConsumption += consumption;
            validPairs++;
            totalDistance += distance;
            totalFuel += fuelAmount;
            totalCost += parseFloat(fillsList[i].price.toString());
            
            lastConsumptions.push(consumption);
          }
        }

        const costPer100km = totalDistance > 0 ? (totalCost / totalDistance) * 100 : 0;
        const avgPricePerLiter = totalFuel > 0 ? totalCost / totalFuel : 0;

        // Calculate trend based on moving average
        if (lastConsumptions.length >= 3) {
          const recentAvg = lastConsumptions.slice(-3).reduce((a, b) => a + b, 0) / 3;
          const previousAvg = lastConsumptions.slice(-6, -3).length > 0 
            ? lastConsumptions.slice(-6, -3).reduce((a, b) => a + b, 0) / lastConsumptions.slice(-6, -3).length 
            : recentAvg;
          
          const diff = recentAvg - previousAvg;
          const newTrend: ConsumptionTrend = 
            Math.abs(diff) < 0.2 ? 'stable' : 
            diff > 0 ? 'up' : 'down';
          setConsumptionTrend(newTrend);
        } else {
          setConsumptionTrend('stable');
        }

        setAvgConsumption(validPairs > 0 ? totalConsumption / validPairs : 0);
        setTotalStats({
          distance: parseFloat(totalDistance.toFixed(1)),
          fuel: parseFloat(totalFuel.toFixed(1)),
          fillCount: fillsList.length,
          totalCost: parseFloat(totalCost.toFixed(2)),
          costPer100km: parseFloat(costPer100km.toFixed(2)),
          avgPricePerLiter: parseFloat(avgPricePerLiter.toFixed(2))
        });
      } else {
        const singleFill = fillsList[0] || { fuelamount: 0, price: 0 };
        const avgPricePerLiter = singleFill.fuelamount > 0 ? singleFill.price / singleFill.fuelamount : 0;
        setTotalStats({ 
          distance: 0, 
          fuel: parseFloat(singleFill.fuelamount.toFixed(1)), 
          fillCount: fillsList.length,
          totalCost: parseFloat(singleFill.price.toFixed(2)),
          costPer100km: 0,
          avgPricePerLiter: parseFloat(avgPricePerLiter.toFixed(2))
        });
        setConsumptionTrend('stable');
      }
    });

    return () => unsubscribe();
  }, [currentUser, selectedCar]);

  const handleCarSelect = async (carId: string) => {
    if (!currentUser) return;
    
    try {
      const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
      await set(selectedCarRef, carId);
      setSelectedCar(carId);
    } catch (error) {
      console.error('Error setting selected car:', error);
    }
  };

  const calculateFuelConsumption = (currentOdometer: number, currentFuelAmount: number) => {
    // Sort fills by odometer in descending order
    const sortedFills = [...fills].sort((a, b) => b.odometer - a.odometer);
    
    // Find the previous fill (next highest odometer reading)
    const previousFill = sortedFills.find(fill => fill.odometer < currentOdometer);
    
    if (previousFill) {
      const distance = currentOdometer - previousFill.odometer;
      const consumption = (currentFuelAmount / distance) * 100; // L/100km
      return consumption;
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !selectedCar) return;

    try {
      const currentOdometer = Number(mileage);
      const currentFuelAmount = Number(amount);

      const fillsRef = ref(database, `cars/${selectedCar}/fills`);
      const newFill = {
        timestamp: Date.now(),
        odometer: currentOdometer,
        fuelamount: currentFuelAmount,
        price: Number(price)
      };
      
      await push(fillsRef, newFill);

      // Calculate consumption
      const consumption = calculateFuelConsumption(currentOdometer, currentFuelAmount);
      setLastConsumption(consumption);
      
      // Clear form
      setMileage('');
      setAmount('');
      setPrice('');

      // Show success toast
      setShowToast(true);
    } catch (error) {
      console.error('Error creating fill:', error);
      alert('Fehler beim Speichern der Tankfüllung. Bitte versuchen Sie es erneut.');
    }
  };

  // Filter owned cars to only show active ones
  const activeCars = ownedCarIds
    .filter(id => cars[id] && cars[id].is_active)
    .map(id => ({
      id,
      ...cars[id]
    }));

  return (
    <Container>
      <ToastContainer 
        className="p-3" 
        position="top-end"
        style={{ zIndex: 1 }}
      >
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={6000} 
          autohide
          bg="success"
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto">Erfolg</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Tankfüllung wurde erfolgreich gespeichert
            {lastConsumption !== null && (
              <div className="mt-1">
                Verbrauch: {lastConsumption.toFixed(1)} l/100km
              </div>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <h2 className="mb-4">Moscht</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht" 
            active={location.pathname === '/dashboard/moscht'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/tankungen" 
            active={location.pathname === '/dashboard/moscht/tankungen'}
          >
            Tankungen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/events" 
            active={location.pathname === '/dashboard/moscht/events'}
          >
            Events
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/logs" 
            active={location.pathname === '/dashboard/moscht/logs'}
          >
            Logs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/cars" 
            active={location.pathname === '/dashboard/moscht/cars'}
          >
            Fahrzeuge
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/settings" 
            active={location.pathname === '/dashboard/moscht/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Fahrzeugauswahl</Card.Title>
              <Form className="mt-3">
                <Form.Group>
                  <Form.Label>Aktuelles Fahrzeug</Form.Label>
                  <Form.Select 
                    value={selectedCar} 
                    onChange={(e) => handleCarSelect(e.target.value)}
                    className="mb-3"
                  >
                    <option value="">Fahrzeug auswählen...</option>
                    {activeCars.map(car => (
                      <option key={car.id} value={car.id}>
                        {car.name}
                      </option>
                    ))}
                  </Form.Select>
                  {!activeCars.length && (
                    <div className="text-muted">
                      Keine aktiven Fahrzeuge verfügbar. Bitte fügen Sie zuerst Fahrzeuge in Ihrem Profil hinzu.
                    </div>
                  )}
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {selectedCar && (
          <Col md={6}>
            <Card className="h-100">
              <Card.Body className="py-4">
                <Card.Title style={{ color: '#1780a6', marginBottom: '1rem' }}>Verbrauch</Card.Title>
                <div className="text-center">
                  {fills.length < 2 ? (
                    <div className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
                      Mindestens zwei Tankfüllungen erforderlich für die Verbrauchsberechnung.
                    </div>
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.875rem', display: 'grid', gap: '0.75rem', padding: '0.5rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'baseline' }}>
                        <strong style={{ color: '#1780a6', gridColumn: '1 / -1' }}>Verbrauch</strong>
                        <span>Ø Verbrauch:</span>
                        <span>
                          {(avgConsumption || 0).toFixed(1)} l/100km
                          {consumptionTrend !== 'stable' && (
                            <span style={{ 
                              marginLeft: '0.25rem',
                              color: consumptionTrend === 'up' ? '#dc3545' : '#198754'
                            }}>
                              {consumptionTrend === 'up' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                        <span>Gesamtverbrauch:</span>
                        <span>{totalStats.fuel.toFixed(1)} l</span>
                      </div>
                      <hr style={{ margin: '0.25rem 2rem', borderColor: '#dee2e6', opacity: 0.2 }} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'baseline' }}>
                        <strong style={{ color: '#1780a6', gridColumn: '1 / -1' }}>Distanz & Tankungen</strong>
                        <span>Gesamtstrecke:</span>
                        <span>{Math.round(totalStats.distance).toLocaleString('de-CH')} km</span>
                        <span>Tankfüllungen:</span>
                        <span>{totalStats.fillCount}</span>
                      </div>
                      <hr style={{ margin: '0.25rem 2rem', borderColor: '#dee2e6', opacity: 0.2 }} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'baseline' }}>
                        <strong style={{ color: '#1780a6', gridColumn: '1 / -1' }}>Kosten</strong>
                        <span>Gesamtkosten:</span>
                        <span>{totalStats.totalCost.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'")} CHF</span>
                        <span>Kosten/100km:</span>
                        <span>{totalStats.costPer100km.toFixed(2)} CHF</span>
                        <span>Ø Preis/Liter:</span>
                        <span>{totalStats.avgPricePerLiter.toFixed(2)} CHF</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {selectedCar && (
        <Card>
          <Card.Body>
            <Card.Title style={{ color: '#1780a6' }}>Tankfüllung erfassen</Card.Title>
            <Form className="mt-3" onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Kilometerstand</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value)}
                    placeholder="0"
                    min="0"
                    required
                  />
                  <InputGroup.Text>km</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Menge</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                  <InputGroup.Text>l</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Preis</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                  <InputGroup.Text>CHF</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Button type="submit" variant="primary">
                Speichern
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default Moscht;
